import { addDays, dayOfWeek, daysInMonth, floorMod, parseIsoDate, startOfMonth } from '../engine/civil'
import type { DayException, IsoDate, ShiftInstance, WeekStart } from '../engine/types'
import { MONTH_LONG, WEEKDAY_SHORT } from '../lib/format-date'
import { EXCEPTION_LABEL } from './ExceptionDayRow'
import { ShiftCodeChip } from './ShiftCodeChip'

export interface MonthGridProps {
  /** Any date within the month to render. */
  month: IsoDate
  /** The shifts to place, typically a whole month from effectiveRoster(). */
  shifts: readonly ShiftInstance[]
  /**
   * Days the block pattern does not apply — leave, unavailable, not working.
   * Rendered as their own small flag, never as a shift chip: an exception is
   * the absence of a shift, and dressing it as one would un-retire exactly
   * the confusion Phase 3 exists to remove. Optional only so the Phase 1
   * callers that pass no data still typecheck; CalendarScreen passes the
   * real rows from Phase 3 on.
   */
  exceptions?: readonly DayException[]
  /** Fired when a day is tapped — CalendarScreen owns the DaySheet state. */
  onDayTap?: (date: IsoDate) => void
  /** Today, passed in — this component never reads the clock. */
  today: IsoDate
  /** Which day the displayed week starts on — the user's own preference. */
  weekStart: WeekStart
  /**
   * Days carrying an OPEN advertisement (Phase 4) — rendered as the "Swap"
   * flag, the grid's one-word grammar the exception flags established. The
   * advertised shift itself still renders its chip: the flag says what the
   * *day* carries, exactly as `Leave` does.
   */
  advertisedDates?: readonly IsoDate[]
}

/** One cell of the grid, or `null` for a leading/trailing blank. */
interface DayCell {
  date: IsoDate
  day: number
}

const WEEK_LENGTH = 7

/**
 * The grid's one-word flags. The agenda row carries the full label
 * (`ExceptionDayRow`'s `EXCEPTION_LABEL`); a ~50px cell does not, so the
 * flag keeps its own shortest honest word and the button's `aria-label`
 * and the flag's `title` both carry the full kind.
 */
const FLAG_SHORT_LABEL: Record<DayException['kind'], string> = {
  LEAVE: 'Leave',
  UNAVAILABLE: 'Away',
  NOT_WORKING: 'Off',
}

/**
 * A month, as a semantic `<table>` — not `role="grid"`.
 *
 * Each day cell holds one plain, natively-focusable `<button>` that fires
 * `onDayTap(date)` — one tap opens the day sheet, so there is nothing for a
 * roving-tabindex helper to do: tabbing walks the days in reading order,
 * which is the order a screen reader wants anyway. Phase 1's comment
 * deferred roving to "when tapping a day does something"; it now does
 * something simple, and plain buttons stay the decision. `src/ui/roving.ts`
 * remains the wrong tool regardless — one-dimensional, vendored, and a
 * closed tab/radio role map — see CLAUDE.md.
 *
 * Weeks start on `weekStart` — Sunday or Monday, the user's own preference.
 * `dayOfWeek` is always `0 = Sunday` regardless, so a Monday start rotates
 * the header display and shifts the leading-blank count by one. That
 * rotation must go through `floorMod`, never `%`: for a month whose 1st
 * falls on a Sunday, a Monday start needs `0 − 1 = −1` folded to `6`, and
 * `%` leaves it `-1` — a negative blank count that would silently shift
 * every cell in the month by one column. See CLAUDE.md's `floorMod` rule.
 */
export function MonthGrid({
  month,
  shifts,
  exceptions = [],
  onDayTap,
  today,
  weekStart,
  advertisedDates = [],
}: MonthGridProps) {
  const first = startOfMonth(month)
  const { year, month: monthNum } = parseIsoDate(first)
  const totalDays = daysInMonth(year, monthNum)
  // 0 = Sunday, matching dayOfWeek's own convention: a Sunday start needs no
  // rotation at all, a Monday start rotates by one column.
  const weekStartOffset = weekStart === 'MONDAY' ? 1 : 0
  const leadingBlanks = floorMod(dayOfWeek(first) - weekStartOffset, 7)
  // WEEKDAY_SHORT stays Sunday-indexed at its source — other code indexes it
  // by dayOfWeek's convention — so the header row rotates only its own local
  // copy for display.
  const headerLabels =
    weekStart === 'MONDAY' ? [...WEEKDAY_SHORT.slice(1), WEEKDAY_SHORT[0]] : WEEKDAY_SHORT

  // One derived shift per (date, code) today — effectiveRoster never
  // produces two on the same day for a block user — but grouped by date
  // defensively rather than assumed, so a future explicit row sharing a
  // date with a derived one still renders both chips instead of one
  // silently winning.
  const shiftsByDate = new Map<IsoDate, ShiftInstance[]>()
  for (const shift of shifts) {
    const existing = shiftsByDate.get(shift.date)
    if (existing) existing.push(shift)
    else shiftsByDate.set(shift.date, [shift])
  }

  // `day_exceptions` is unique on (owner, date), so one exception per day is
  // the shape the database enforces — but first-wins here rather than
  // assumed, so a hand-built fixture with two still renders deterministically.
  const exceptionByDate = new Map<IsoDate, DayException>()
  for (const exception of exceptions) {
    if (!exceptionByDate.has(exception.date)) exceptionByDate.set(exception.date, exception)
  }

  const advertisedDaySet = new Set<IsoDate>(advertisedDates)

  const days: DayCell[] = []
  for (let offset = 0; offset < totalDays; offset += 1) {
    days.push({ date: addDays(first, offset), day: offset + 1 })
  }

  const trailingBlanks =
    (WEEK_LENGTH - ((leadingBlanks + totalDays) % WEEK_LENGTH)) % WEEK_LENGTH

  const cells: Array<DayCell | null> = [
    ...Array<null>(leadingBlanks).fill(null),
    ...days,
    ...Array<null>(trailingBlanks).fill(null),
  ]

  const weeks: Array<Array<DayCell | null>> = []
  for (let index = 0; index < cells.length; index += WEEK_LENGTH) {
    weeks.push(cells.slice(index, index + WEEK_LENGTH))
  }

  /** 'Fri 18 September' — the same words `formatShortDate` spells, with the
      month unabbreviated, because an aria-label is heard, not scanned. */
  const spokenDate = (date: IsoDate): string => {
    const { month: m, day } = parseIsoDate(date)
    return `${WEEKDAY_SHORT[dayOfWeek(date)]} ${day} ${MONTH_LONG[m - 1]}`
  }

  return (
    <table className="ss-month-grid">
      {/* Visually hidden: CalendarScreen already shows a heading naming the
          month above this table, so a second, visible caption would repeat
          it. The name still reaches assistive tech, which is what the
          caption element is for. */}
      <caption className="sl-visually-hidden">
        {MONTH_LONG[monthNum - 1]} {year}
      </caption>
      <thead>
        <tr>
          {headerLabels.map((weekday) => (
            <th key={weekday} scope="col">
              {weekday}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {weeks.map((week, weekIndex) => (
          <tr key={weekIndex}>
            {week.map((cell, cellIndex) => {
              if (cell === null) {
                // Inert to assistive tech — a blank cell has nothing to
                // announce, and aria-hidden keeps a screen reader from
                // trying anyway. No button here: a blank is not a day.
                return (
                  <td
                    key={cellIndex}
                    className="ss-month-grid__cell ss-month-grid__cell--blank"
                    aria-hidden="true"
                  />
                )
              }

              const isToday = cell.date === today
              const cellShifts = shiftsByDate.get(cell.date) ?? []
              const cellException = exceptionByDate.get(cell.date)
              const isAdvertised = advertisedDaySet.has(cell.date)

              // What the day carries, in the words the aria-label uses —
              // the chip's own letters plus "shift", the exception's full
              // kind, and "advertised" when the day's shift is on the feed.
              // A day with nothing on it is named and left there.
              const carried = cellException
                ? EXCEPTION_LABEL[cellException.kind].toLowerCase()
                : cellShifts.length > 0
                  ? cellShifts.map((shift) => `${shift.code} shift`).join(', ')
                  : undefined
              const carrying = isAdvertised
                ? carried === undefined
                  ? 'advertised for swap'
                  : `${carried}, advertised for swap`
                : carried
              const label = carrying
                ? `${spokenDate(cell.date)}, ${carrying}`
                : spokenDate(cell.date)

              return (
                <td
                  key={cell.date}
                  className={
                    isToday
                      ? 'ss-month-grid__cell ss-month-grid__cell--today'
                      : 'ss-month-grid__cell'
                  }
                  aria-current={isToday ? 'date' : undefined}
                >
                  <button
                    type="button"
                    className="ss-month-grid__day"
                    aria-label={label}
                    onClick={onDayTap ? () => onDayTap(cell.date) : undefined}
                  >
                    <span className="ss-month-grid__daynum">{cell.day}</span>
                    {cellException ? (
                      <span
                        className="ss-month-grid__flag"
                        data-kind={cellException.kind}
                        title={EXCEPTION_LABEL[cellException.kind]}
                      >
                        {FLAG_SHORT_LABEL[cellException.kind]}
                      </span>
                    ) : null}
                    {isAdvertised ? (
                      <span
                        className="ss-month-grid__flag ss-month-grid__flag--advertised"
                        title="Advertised for swap"
                      >
                        Swap
                      </span>
                    ) : null}
                    {cellShifts.map((shift) => (
                      <ShiftCodeChip key={shift.code} code={shift.code} />
                    ))}
                  </button>
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
